import { makeStyles } from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '~components/tooltip';
import Typography from '~components/typography';
import { TRANSACTION_CATEGORY_OPTIONS, TRANSACTION_TYPE_OPTIONS } from '~graphql-types';
import {
  BasicCommercialFilled,
  BasicDiscountFilled,
  BasicHome,
  BasicInstitutionFilled,
  BasicOther,
  BasicServicesFoodFilled,
  BasicShoppingCartFilled,
  NavMaintenance,
  SettingsDeposit,
} from '~static/svg-components';
import { FORMAT_YEAR_MONTH_DATE_HOUR_MINUTE } from '~utils/date-format';
import { toThousands } from '~utils/to-thousands';
import ExternalIdChip from './external-id-chip';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      display: 'flex',
      alignItems: 'stretch',
      minWidth: '360px',
      padding: '12px 12px 12px 16px',
      minHeight: '70px',
      marginBottom: '12px',
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.small,
      border: `1px solid ${theme.colors.greyE8}`,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.colors.greyFa,
      },
    },

    nameDateAmount: {
      flex: 1,
    },

    name: {
      ...theme.flex.crossCenter,
    },

    categoryIcon: {
      ...theme.flex.center,
      width: 20,
      height: 20,
      marginLeft: 8,
      backgroundColor: theme.colors.greyE8,
      borderRadius: theme.borderRadius.full,
      '& svg': {
        width: 12,
        height: 12,
        fill: theme.colors.grey66,
      },
    },

    dateTimeAndType: {
      ...theme.flex.crossCenter,
    },

    divider: {
      margin: '0 8px',
    },

    lined: {
      margin: '0 4px',
    },

    amountChip: {
      ...theme.flex.col,
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      minWidth: '60px',
      textAlign: 'right',
    },

    chip: {
      width: '100%',
      bottom: 0,
    },

    channelAndMethod: {
      maxWidth: '280px',
      whiteSpace: 'nowrap',
      wordBreak: 'break-all',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
  }),
  { name: 'TransactionRecord' }
);

function TransactionRecord({ transactionRecord }) {
  const { t } = useTranslation(['graphql']);
  const classes = useStyles();

  const {
    name,
    transactionDateTime,
    category,
    type,
    amount,
    referenceInfo,
    propertyId,
    bookingId,
    description,
    method,
    channel,
    originId,
  } = transactionRecord;

  const paymentsCategoryIcon = () => {
    switch (category) {
      case TRANSACTION_CATEGORY_OPTIONS.ADMINISTRATION_FEE:
        return <SettingsDeposit />;
      case TRANSACTION_CATEGORY_OPTIONS.COMMERCIAL:
        return <BasicCommercialFilled />;
      case TRANSACTION_CATEGORY_OPTIONS.CONSUMABLE:
        return <BasicShoppingCartFilled />;
      case TRANSACTION_CATEGORY_OPTIONS.DAMAGE:
        return <NavMaintenance />;
      case TRANSACTION_CATEGORY_OPTIONS.DISCOUNT:
        return <BasicDiscountFilled />;
      case TRANSACTION_CATEGORY_OPTIONS.NOMINATION:
        return <BasicInstitutionFilled />;
      case TRANSACTION_CATEGORY_OPTIONS.OTHER:
        return <BasicOther />;
      case TRANSACTION_CATEGORY_OPTIONS.RENTAL:
        return <BasicHome />;
      case TRANSACTION_CATEGORY_OPTIONS.SERVICE:
        return <BasicServicesFoodFilled />;
      case null:
        return <BasicOther />;

      default:
        break;
    }
  };

  const renderNameAndDescriptionContent = () => {
    if (!name && !description) return '-';
    if (name && description) return `${name} - ${description}`;
    return `${name || ''}${description || ''}`;
  };

  const renderName = () => (
    <Typography variant="body1" themeColor="dark" className={classes.name}>
      {renderNameAndDescriptionContent()}
      <Tooltip title={t(`graphql:transaction_category.${category || 'OTHER'}`)} placement="top">
        <span className={classes.categoryIcon}>{paymentsCategoryIcon()}</span>
      </Tooltip>
    </Typography>
  );

  const renderDatetimeAndType = () => (
    <div className={classes.dateTimeAndType}>
      <Typography variant="body2" themeColor="grey66" className={classes.dateTime}>
        {format(parseISO(transactionDateTime), FORMAT_YEAR_MONTH_DATE_HOUR_MINUTE)}
      </Typography>
      <Typography variant="body1" themeColor="greyE8" className={classes.divider}>
        |
      </Typography>
      <Typography variant="body2" themeColor="grey66" className={classes.type}>
        {t(`graphql:transaction_type.${type}`)}
      </Typography>

      {(method || channel) && (
        <Typography variant="body1" themeColor="greyE8" className={classes.divider}>
          |
        </Typography>
      )}

      <div>
        {(method || channel) && (
          <Typography variant="body2" themeColor="grey66" className={classes.channelAndMethod}>
            {method && t(`transaction_method.${method}`)} {method && channel && ' - '}
            {channel && t(`transaction_channel.${channel}`)}
          </Typography>
        )}
      </div>
    </div>
  );

  const renderReferenceInfo = () => (
    <Typography variant="body2" themeColor="grey66" className={classes.type}>
      {referenceInfo}
    </Typography>
  );

  const renderAmountAndChip = () => {
    const amountWithThousandsSeparator = toThousands(amount);
    let color = 'grey99';
    let value = `(${amountWithThousandsSeparator})`;

    if (
      [
        TRANSACTION_TYPE_OPTIONS.CHARGE,
        TRANSACTION_TYPE_OPTIONS.REFUND,
        TRANSACTION_TYPE_OPTIONS.REQUEST,
        TRANSACTION_TYPE_OPTIONS.REVERSAL_PAYMENT,
      ].includes(type)
    ) {
      color = 'redDark';
      value = `- ${amountWithThousandsSeparator}`;
    } else if (
      [TRANSACTION_TYPE_OPTIONS.PAYMENT, TRANSACTION_TYPE_OPTIONS.TRANSFER_FROM].includes(type)
    ) {
      color = 'greenDark';
      value = `+ ${amountWithThousandsSeparator}`;
    }
    if (referenceInfo?.indexOf('target_booking_id') > -1) {
      color = 'redDark';
      value = `- ${amountWithThousandsSeparator}`;
    }

    return (
      <div className={classes.amountChip}>
        <Typography component="span" variant="h5" themeColor={color} className={classes.iconOnLeft}>
          {value}
        </Typography>
        <ExternalIdChip className={classes.chip} cardId={originId} hideTooltip />
      </div>
    );
  };

  const renderMainTransactionRecordInfo = () => (
    <div className={classes.nameDateAmount}>
      {renderName()}
      {renderDatetimeAndType()}
      {referenceInfo && renderReferenceInfo()}
    </div>
  );

  const openBookingDetailPage = () => {
    const bookingDetailsUrl = queryString.stringifyUrl({
      url: '/booking/booking-details',
      query: { propertyId, bookingId },
    });
    window.open(bookingDetailsUrl);
  };

  return (
    <div
      className={classes.root}
      onClick={() => {
        openBookingDetailPage();
      }}
    >
      {renderMainTransactionRecordInfo()}
      {renderAmountAndChip()}
    </div>
  );
}

TransactionRecord.prototype = {
  transactionRecord: PropTypes.object.isRequired,
};

export default TransactionRecord;
